import React, { useMemo } from 'react';
import { List, Typography } from 'antd';
import type { Quotation } from '@/services/quotation.service';
import { displayQuotationRevisionLabel } from '@/utils/quotationRevisionPolicy';
import dayjs from 'dayjs';

const { Text } = Typography;

type Props = {
  quotation: Quotation;
  family: Quotation[];
};

export const QuotationActivityTab: React.FC<Props> = ({ quotation, family }) => {
  const events = useMemo(() => {
    const rows: { at: string; text: string }[] = [];

    for (const r of family) {
      const rev = displayQuotationRevisionLabel(r.revision_number);
      if (r.created_at) {
        rows.push({
          at: r.created_at,
          text: `${rev} created${r.parent_quotation_id ? ' from prior revision' : ''}`,
        });
      }
      if (r.revision_type) {
        rows.push({ at: r.updated_at || r.created_at, text: `${rev} type: ${r.revision_type}` });
      }
      if (r.approval_status === 'Approved' && r.approved_date) {
        rows.push({ at: r.approved_date, text: `${rev} approved` });
      }
      if (r.sent_date) {
        rows.push({ at: r.sent_date, text: `${rev} submitted to customer` });
      }
      if (r.superseded_at) {
        rows.push({ at: r.superseded_at, text: `Prior offer superseded when ${rev} sent` });
      }
      if (r.acceptance_date) {
        rows.push({ at: r.acceptance_date, text: `${rev} customer accepted` });
      }
    }

    if (quotation.revision_notes) {
      rows.push({
        at: quotation.updated_at,
        text: `Notes: ${quotation.revision_notes}`,
      });
    }

    return rows.sort((a, b) => dayjs(b.at).valueOf() - dayjs(a.at).valueOf());
  }, [quotation, family]);

  if (events.length === 0) {
    return <Text type="secondary">No activity recorded for this revision family yet.</Text>;
  }

  return (
    <List
      size="small"
      dataSource={events}
      renderItem={(item) => (
        <List.Item>
          <List.Item.Meta
            title={item.text}
            description={dayjs(item.at).format('DD MMM YYYY HH:mm')}
          />
        </List.Item>
      )}
    />
  );
};

export default QuotationActivityTab;
